import { useState } from 'react';
import { Stack } from '@koob/margaret';
import { Trans, useTranslation } from 'react-i18next';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { useError, useSnack } from 'hooks';
import { KoobButton, KoobInputTextareaField, KoobCard } from '@koob/ui';
import { Modal } from 'components/index';
import { Text } from 'ui';
import { FormLabel } from 'ui/forms';
import { cancelBooking } from '../../../../api/bookings';
import { useKoobStore } from 'store';
import { LinkedBookingHotels } from '@koob/booking';

const CancelAdminBooking = ({
  adminBookingId,
  kind,
  reload,
  linkedBookingHotels = [],
}) => {
  const { t } = useTranslation('booking');
  const { notify } = useSnack();
  const { sendErrorSnack } = useError();
  const [isOpen, setIsOpen] = useState(false);
  const refreshFolder = useKoobStore(s => s.refreshFolder);

  const onSubmit = async ({ comment }) => {
    try {
      await cancelBooking({ bookingId: adminBookingId, reason: comment });
      reload();
      // Keep the sidebar active-folder card in sync after a cancel (US-014).
      refreshFolder();
      notify(t('form.cancelSuccess'));
    } catch (err) {
      sendErrorSnack(err);
    }
    setIsOpen(false);
  };

  return (
    <>
      <KoobCard tone="muted">
        <Stack
          alignX="space-between"
          alignY="center"
          style={{
            opacity: 'unset',
            background: 'unset',
            backgroundColor: 'unset',
          }}
        >
          <Stack direction="column">
            <FormLabel>{t('form.cancelBooking')}</FormLabel>
            <Text>{t('form.cancelInfo')}</Text>
          </Stack>
          <Stack direction="column">
            <KoobButton
              type="button"
              variant="danger"
              testId="cancel-booking"
              onClick={() => setIsOpen(true)}
            >
              {t('misc:cancel')}
            </KoobButton>
          </Stack>
        </Stack>
      </KoobCard>
      {isOpen && (
        <Modal
          size={!linkedBookingHotels.length ? 'small' : undefined}
          title={t('misc:warning')}
          isOpen={isOpen}
          onRequestClose={() => setIsOpen(false)}
        >
          <Trans i18nKey="warningCancelModal">
            You are about to cancel this <strong>booking</strong>, are you sure
            you want to continue?
          </Trans>

          {linkedBookingHotels.length > 0 && (
            <LinkedBookingHotels
              hotels={linkedBookingHotels}
              title={t('associatedHotelsNotAutoCancelled', {
                count: linkedBookingHotels.length,
              })}
            />
          )}

          <Formik
            initialValues={{ comment: '' }}
            validationSchema={Yup.object().shape({
              comment:
                kind === 'DMC'
                  ? Yup.string().required(t('errors:required'))
                  : Yup.string(),
            })}
            enableReinitialize
            onSubmit={onSubmit}
          >
            {({ isSubmitting }) => (
              <Form>
                <Stack
                  size="full"
                  direction="column"
                  paddingTop={2}
                  gutterSize={2}
                >
                  {kind === 'DMC' && (
                    <KoobInputTextareaField
                      name="comment"
                      label={t('form.reason')}
                    />
                  )}
                  <Stack size="full" alignX="flex-end" gutterSize={1}>
                    <KoobButton
                      type="button"
                      variant="secondary"
                      onClick={() => setIsOpen(false)}
                    >
                      {t('misc:cancel')}
                    </KoobButton>
                    <KoobButton
                      variant="danger"
                      loading={isSubmitting}
                      type="submit"
                      testId="continue-cancel"
                    >
                      {t('misc:continue')}
                    </KoobButton>
                  </Stack>
                </Stack>
              </Form>
            )}
          </Formik>
        </Modal>
      )}
    </>
  );
};

export default CancelAdminBooking;
